'use client';

/**
 * AbSplitView — full-width live A/B test comparison.
 *
 * The two variants of an active test load side by side as REAL live pages
 * (proxied through /api/funnels/preview to bypass X-Frame-Options), each with
 * its numbers above, independently scrollable. Boss picks the winner visually;
 * the pick is stored locally — applying it = code change in the funnel repo,
 * pushed to git and shipped with the Deploy button (standing policy: deploys
 * are always a button press, never automatic).
 */
import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Eye, EyeOff, Crown, ChevronDown, RotateCcw } from 'lucide-react';
import { isElectron } from '@/lib/api';

interface AbVariant {
  variantId: number; variantKey: string; variantName: string;
  isControl: boolean; assignments: number; conversions: number; conversionRate: number;
}
interface AbTest {
  id: number; testName: string; pagePath: string; status: string;
  winnerVariantId: number | null; description?: string; totalAssignments: number;
  variants: AbVariant[];
}

/** Variant page URL: funnel routes follow /<pagePath>/variant-<key> (verified live). */
function variantUrl(origin: string, t: AbTest, v: AbVariant): string {
  return `${origin}${t.pagePath}/variant-${v.variantKey.toLowerCase()}`;
}

export function AbSplitView({ funnelId, origin, tests }: {
  funnelId: string;
  origin: string;    // funnel site origin, e.g. https://yevlakhova.com
  tests: AbTest[];
}) {
  const [testId, setTestId] = useState<number | null>(tests[0]?.id ?? null);
  const [live, setLive] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const test = useMemo(() => tests.find(t => t.id === testId) || tests[0], [tests, testId]);

  // winner pick is local until it ships via git+Deploy
  const pickKey = `ab-winner-${funnelId}-${test?.id}`;
  const [picked, setPicked] = useState<number | null>(null);
  useEffect(() => {
    if (!test) return;
    const v = localStorage.getItem(pickKey);
    setPicked(v ? Number(v) : null);
  }, [pickKey, test]);
  const pick = (variantId: number) => {
    const next = picked === variantId ? null : variantId;
    setPicked(next);
    if (next == null) localStorage.removeItem(pickKey);
    else localStorage.setItem(pickKey, String(next));
  };

  if (!test) return <p className="text-xs text-gray-500">No A/B tests.</p>;
  const [left, right] = [test.variants[1], test.variants[0]]; // B left, A/control right (Boss's layout)
  // >100% conversion = tracking bug (event overcount); never crown a "best" rate on bad data
  const dataValid = test.variants.every(v => v.conversionRate >= 0 && v.conversionRate <= 100);
  const lowSample = (test.totalAssignments ?? 0) < 100;
  const bestRate = Math.max(...test.variants.map(v => v.conversionRate));

  const VariantHead = ({ v }: { v: AbVariant }) => (
    <div className={`flex items-center gap-3 rounded-t-lg border px-3 py-2 ${
      picked === v.variantId ? 'border-amber-500/60 bg-amber-500/10' : 'border-white/[0.08] bg-white/[0.03]'
    }`}>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-white truncate">
          {v.variantKey}: {v.variantName}{v.isControl ? ' (control)' : ''}
        </p>
        <p className="text-[10px] text-gray-500">{v.assignments} assigned · {v.conversions} conv</p>
      </div>
      {dataValid ? (
        <span className={`text-lg font-bold ${v.conversionRate === bestRate && !lowSample ? 'text-green-400' : 'text-cyan-300'}`}>
          {v.conversionRate}%
        </span>
      ) : (
        <span className="text-[10px] font-semibold text-amber-400 leading-tight text-right"
          title="Conversions exceed assignments — the tracker counts events, not people (repeat bookings / reloads / untracked visitors). Rate is meaningless until fixed.">
          ⚠ invalid<br />tracking
        </span>
      )}
      <span className="flex-1" />
      <a href={variantUrl(origin, test, v)} target="_blank" rel="noopener noreferrer"
        className="text-gray-500 hover:text-white" title="Open in browser">
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
      <button onClick={() => pick(v.variantId)}
        className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${
          picked === v.variantId
            ? 'bg-amber-500/25 text-amber-300'
            : 'bg-white/5 text-gray-400 hover:text-amber-300 hover:bg-amber-500/10'
        }`}>
        <Crown className="w-3 h-3" /> {picked === v.variantId ? 'Winner ✓' : 'Pick winner'}
      </button>
    </div>
  );

  return (
    <div>
      {/* toolbar: test picker + live toggle */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {tests.length > 1 ? (
          <div className="relative">
            <select value={test.id} onChange={e => { setTestId(Number(e.target.value)); setLive(false); }}
              className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-8 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer">
              {tests.map(t => <option key={t.id} value={t.id}>{t.testName} — {t.pagePath}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
          </div>
        ) : (
          <p className="text-xs font-semibold text-white">{test.testName} <span className="text-gray-600">{test.pagePath}</span></p>
        )}
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
          test.status === 'active' ? 'bg-green-500/15 text-green-300' : 'bg-gray-500/15 text-gray-400'
        }`}>{test.status}</span>
        {test.description && <span className="text-[10px] text-gray-600">{test.description}</span>}
        <span className="flex-1" />
        {live && (
          <button onClick={() => setReloadKey(k => k + 1)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 text-gray-400 hover:text-white text-xs transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Reload
          </button>
        )}
        <button onClick={() => setLive(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            live ? 'bg-red-500/15 text-red-300 hover:bg-red-500/25' : 'bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25'
          }`}>
          {live ? <><EyeOff className="w-3.5 h-3.5" /> Close live</> : <><Eye className="w-3.5 h-3.5" /> Open live</>}
        </button>
      </div>

      {/* data-quality verdict — shown before any number can mislead */}
      {(!dataValid || lowSample) && (
        <div className="mb-3 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300/90">
          {!dataValid && <p>⚠ <b>Tracking bug:</b> conversions exceed assignments — the funnel counts booking <i>events</i>, not converting <i>people</i> (repeat bookings, reloads, untracked visitors). These rates are not real until the provider fixes counting.</p>}
          {lowSample && <p>⚠ <b>Too little data:</b> {test.totalAssignments ?? 0} total assignments — no winner can be called under ~100+ per variant. Differences at this sample size are noise.</p>}
        </div>
      )}

      {/* split: B left, A right — live pages or compact stat cards */}
      <div className="grid grid-cols-2 gap-3">
        {[left, right].filter(Boolean).map(v => (
          <div key={v.variantId} className="min-w-0">
            <VariantHead v={v} />
            {live ? (
              <iframe
                key={`${v.variantId}-${reloadKey}`}
                // Electron strips X-Frame-Options for funnel hosts (main.ts) → fully
                // live page. Plain browsers fall back to the script-stripped proxy.
                src={isElectron
                  ? variantUrl(origin, test, v)
                  : `/api/funnels/preview?url=${encodeURIComponent(variantUrl(origin, test, v))}`}
                className="w-full rounded-b-lg border border-t-0 border-white/[0.08] bg-white"
                style={{ height: '75vh' }}
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <div className="rounded-b-lg border border-t-0 border-white/[0.08] bg-white/[0.02] px-3 py-6 text-center">
                <p className="text-[11px] text-gray-600">Hit <span className="text-cyan-300">Open live</span> to load the real page here</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {picked != null && (
        <p className="text-[11px] text-amber-300/90 mt-2 flex items-center gap-1.5">
          <Crown className="w-3.5 h-3.5" />
          Winner picked (saved locally). Applying it = change in the funnel repo → push → Deploy button, as with every site.
        </p>
      )}
    </div>
  );
}
